import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DataTable from "examples/Tables/DataTableV2";

function Visualize({ park }) {
    const [parkPeriods, setParkPeriods] = useState([]);
    const [parkLatitude, setParkLatitude] = useState(0);
    const [parkLongitude, setParkLongitude] = useState(0);
    const [parkStatus, setParkStatus] = useState(0);

    const [contactsEnabled, setContactsEnabled] = useState([true, false, false]); // contact 1 actif par défaut
    const [contacts, setContacts] = useState([]);

    useEffect(() => {
        if (park) {
            setParkStatus(park.status.toUpperCase());

            const [lat, lng] = park.position.split(',').map(coord => parseFloat(coord.trim()));
            setParkLatitude(lat.toFixed(5));
            setParkLongitude(lng.toFixed(5));

            // Parser les périodes et les convertir
            const parsePeriodString = (periodString) => {
                if (!periodString || typeof periodString !== 'string') return [];
                return periodString
                    .split('],')
                    .map(p => {
                        const matches = p.match(/(\d{4}-\d{2}-\d{2})/g);
                        if (matches && matches.length === 2) {
                            return `[ ${matches[0]}, ${matches[1]} ]`;
                        }
                        return null;
                    }).filter(Boolean);
            };

            setParkPeriods(Array.isArray(park.period) ? park.period : parsePeriodString(park.period));

            // Mise à jour des contacts
            if (park.contacts && Array.isArray(park.contacts)) {
                setContacts(park.contacts);
                const length = park.contacts.length;
                setContactsEnabled([
                    length > 0,
                    length > 1,
                    length > 2
                ]);
            }
        }
    }, [park]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return "";

        // Supprimer tous les caractères non numériques
        let cleaned = phone.replace(/\D/g, "");
        // Si le numéro commence par 0033, on retire les 4 premiers caractères
        if (cleaned.startsWith("0033")) {
            cleaned = cleaned.slice(4);
        }
        // S'assurer de ne garder que les 9 derniers chiffres
        cleaned = cleaned.slice(-9);
        return cleaned;
    };

    if (!park) {
        return <MDTypography variant="h6">Chargement des données ...</MDTypography>;
    }

    return (
        <MDBox mt={5} pb={3}>
            <Grid container spacing={1}>
                <Grid item xs={24} md={12} xl={6} sx={{ display: "flex" }}>
                    <MDBox display="flex" flexDirection="column" width="100%" px={2}>
                        <MDBox mb={2} width="96%">
                            <MDInput
                                type="text"
                                label="Name"
                                fullWidth
                                value={park.name}
                                inputProps={{ readOnly: true, color: "dark" }}
                                InputLabelProps={{
                                    shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="96%">
                            <MDInput
                                type="text"
                                label="Address"
                                fullWidth
                                value={park.address}
                                inputProps={{ readOnly: true, color: "dark" }}
                                InputLabelProps={{
                                    shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                }}
                            />
                        </MDBox>
                        <MDBox display="flex" width="96%" mb={2} justifyContent="space-between">
                            <MDBox width="48%">
                                <MDInput
                                    type="text"
                                    label="Latitude"
                                    fullWidth
                                    value={parkLatitude}
                                    inputProps={{ readOnly: true, color: "dark" }}
                                    InputLabelProps={{
                                        shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                    }}
                                />
                            </MDBox>
                            <MDBox width="48%">
                                <MDInput
                                    type="text"
                                    label="Longitude"
                                    fullWidth
                                    value={parkLongitude}
                                    inputProps={{ readOnly: true, color: "dark" }}
                                    InputLabelProps={{
                                        shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                    }}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox display="flex" width="22%" justifyContent="space-between" alignItems="flex-start" ml={1.5} mt={2} mb={2}>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                color="dark"
                                fontSize="0.9rem"
                            >
                                {"MAIN Contact"}
                            </MDTypography>
                            <Tooltip
                                title="Alerts associated with this park will be notified to one or several contacts."
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: "#d8dadd",
                                            color: "#344767",
                                            fontSize: "0.85rem",
                                            fontWeight: 'bold',
                                            borderRadius: 3,
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: "#d8dadd",
                                        },
                                    },
                                }}
                            >
                                <Icon
                                    color="secondary"
                                    fontSize="small"
                                >
                                    info_outline
                                </Icon>
                            </Tooltip>
                        </MDBox>
                        <MDBox mb={2} width="40%">
                            <MDInput
                                type="tel"
                                label="Phone to Notify"
                                fullWidth
                                value={formatPhoneNumber(contacts[0]?.notifPhone) || ""}
                                inputProps={{ readOnly: true, color: "dark" }}
                                InputLabelProps={{
                                    shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                +33
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="70%">
                            <MDInput
                                type="email"
                                label="Email to Notify"
                                fullWidth
                                value={contacts[0]?.notifEmail || ""}
                                inputProps={{ readOnly: true, color: "dark" }}
                                InputLabelProps={{
                                    shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                }}
                            />
                        </MDBox>
                        {/* Additional Contact 2 */}
                        {contactsEnabled[1] && (
                            <>
                                <MDBox mt={2.5} ml={1.5} mb={1.5} display="flex" justifyContent="space-between" alignItems="center" width="94%">
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        color="dark"
                                        fontSize="0.9rem"
                                    >
                                        {"Additional Contact"}
                                    </MDTypography>
                                </MDBox>

                                <MDBox mb={2} width="40%">
                                    <MDInput
                                        type="tel"
                                        label="Phone to Notify"
                                        fullWidth
                                        value={formatPhoneNumber(contacts[1]?.notifPhone) || ""}
                                        inputProps={{ readOnly: true, color: "dark" }}
                                        InputLabelProps={{
                                            shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                        +33
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>

                                <MDBox mb={2} width="70%">
                                    <MDInput
                                        type="email"
                                        label="Email to Notify"
                                        fullWidth
                                        value={contacts[1]?.notifEmail || ""}
                                        inputProps={{ readOnly: true, color: "dark" }}
                                        InputLabelProps={{
                                            shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                        }}
                                    />
                                </MDBox>
                            </>
                        )}

                        {/* Additional Contact 2 */}
                        {contactsEnabled[2] && (
                            <>
                                <MDBox mt={2.5} ml={1.5} mb={1.5} display="flex" justifyContent="space-between" alignItems="center" width="94%">
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        color="dark"
                                        fontSize="0.9rem"
                                    >
                                        {"Additional Contact"}
                                    </MDTypography>
                                </MDBox>

                                <MDBox mb={2} width="40%">
                                    <MDInput
                                        type="tel"
                                        label="Phone to Notify"
                                        fullWidth
                                        value={formatPhoneNumber(contacts[2]?.notifPhone) || ""}
                                        inputProps={{ readOnly: true, color: "dark" }}
                                        InputLabelProps={{
                                            shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                        +33
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>

                                <MDBox mb={2} width="70%">
                                    <MDInput
                                        type="email"
                                        label="Email to Notify"
                                        fullWidth
                                        value={contacts[2]?.notifEmail || ""}
                                        inputProps={{ readOnly: true, color: "dark" }}
                                        InputLabelProps={{
                                            shrink: true, // Force le label à rester au-dessus même si l'input est vide
                                        }}
                                    />
                                </MDBox>
                            </>
                        )}
                    </MDBox>
                </Grid>
                <Grid item xs={24} md={12} xl={5.8}>
                    <MDBox pt={1} pb={2.5}>
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Card>
                                    <MDBox
                                        mx={2}
                                        mt={-3}
                                        py={1.5}
                                        px={2}
                                        variant="gradient"
                                        bgColor="info"
                                        borderRadius="lg"
                                        coloredShadow="info"
                                    >
                                        <MDTypography variant="h6" color="white">
                                            Bridage Agricole Periods
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox pt={3}>
                                        <DataTable
                                            table={{
                                                columns: [
                                                    { Header: "", accessor: "name", align: "left" },
                                                ],
                                                rows: parkPeriods.length > 0
                                                    ? parkPeriods.map((period, index) => ({
                                                        name: (
                                                            <MDTypography key={index} component="span" variant="caption" color="dark" fontWeight="medium">
                                                                {period}
                                                            </MDTypography>
                                                        )
                                                    }))
                                                    : [{
                                                        name: (
                                                            <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
                                                                {"..."}
                                                            </MDTypography>
                                                        )
                                                    }],
                                            }}
                                            rowsNumberPerPage={5}
                                            isSorted={false}
                                            entriesPerPage={false}
                                            showTotalEntries={false}
                                            noEndBorder
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                    <MDBox display="flex" width="30%" justifyContent="space-between" alignItems="flex-start" ml={1.5} mt={2.5} mb={2}>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="dark"
                            fontSize="0.9rem"
                        >
                            {"Park Specification"}
                        </MDTypography>
                    </MDBox>
                    <MDBox display="flex" width="40%" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <MDInput
                            type="tel"
                            label="Observation Radius (m)"
                            fullWidth
                            value={park.distance}
                            inputProps={{ readOnly: true, color: "dark" }}
                            InputLabelProps={{
                                shrink: true, // Force le label à rester au-dessus même si l'input est vide
                            }}
                        />
                        <Tooltip
                            title="This is the observation radius surrounding the wind turbine."
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: "#d8dadd",
                                        color: "#344767",
                                        fontSize: "0.85rem",
                                        fontWeight: 'bold',
                                        borderRadius: 3,
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: "#d8dadd",
                                    },
                                },
                            }}
                        >
                            <Icon
                                color="secondary"
                                fontSize="small"
                                sx={{ ml: 1, mt: 1 }}>
                                info_outline
                            </Icon>
                        </Tooltip>
                    </MDBox>
                    <MDBox mb={2} width="100%">
                        <MDInput
                            type="text"
                            label="Status"
                            fullWidth
                            value={parkStatus}
                            inputProps={{ readOnly: true, color: "dark" }}
                            InputLabelProps={{
                                shrink: true, // Force le label à rester au-dessus même si l'input est vide
                            }}
                        />
                    </MDBox>
                </Grid>
            </Grid>
        </MDBox>
    );
}

export default Visualize;